// SPDX-License-Identifier: MIT
// The « j'ai tout ce qu'il me faut » readiness gauge (guided first-run beats ③/⑤).
// Pure presentation bound to the existing readiness() heuristic — the ink filling
// toward "ready" as the project definition gets richer. `deltaFrom` renders the
// climb (e.g. 51 → 88 %) so the discovery answers visibly NOURISH the regeneration.
// Honest, never a fake progress bar. Styles: guided-first-run.css (.readiness-gauge*).
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import type { Readiness, ReadinessState } from '../onboarding/readiness';

export interface ReadinessGaugeOptions {
  deltaFrom?: number; // when set, the label reads "{deltaFrom} → {pct} %"
}

const LABEL_KEY: Record<ReadinessState, string> = {
  building: 'onboarding.gaugeBuilding',
  almost: 'onboarding.gaugeAlmost',
  ready: 'onboarding.gaugeReady',
};

export function renderReadinessGauge(r: Readiness, opts: ReadinessGaugeOptions = {}): HTMLElement {
  const root = document.createElement('div');
  // `gfr` makes the gauge SELF-SCOPING: guided-first-run.css defines its token
  // aliases (--orange/--ink/--r-pill/--serif/--mono) + selectors under `.gfr`. In
  // the production card the gauge mounts with no `.gfr` ancestor, so the root must
  // carry `.gfr` itself or it renders unstyled (the harness-vs-prod fidelity trap,
  // same fix as the folded discovery).
  root.className = 'readiness-gauge gfr';
  root.setAttribute('data-component', 'readiness-gauge');
  root.setAttribute('data-state', r.state);
  root.setAttribute('role', 'progressbar');
  root.setAttribute('aria-valuenow', String(r.pct));
  root.setAttribute('aria-valuemin', '0');
  root.setAttribute('aria-valuemax', '100');
  const pctText = opts.deltaFrom != null ? `${opts.deltaFrom} → ${r.pct} %` : `${r.pct} %`;
  root.innerHTML = `
    <div class="readiness-gauge__track"><div class="readiness-gauge__fill" style="width:${r.pct}%"></div></div>
    <div class="readiness-gauge__lab"><b>${escapeHtml(t(LABEL_KEY[r.state]))}</b><i>${escapeHtml(pctText)}</i></div>
  `;
  return root;
}
